import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSave } from 'react-icons/fa';
import { FaPlus } from 'react-icons/fa';
import { Button, Modal, ModalMethods, Input, toasts, Loader } from '../../../ui-component';
import { api } from '../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../utils/common';

type OrderTypeItem = {
  id: number;
  name: string;
  active: boolean;
  custom: boolean;
};

type CategoryState = {
  enabled: boolean;
  types: OrderTypeItem[];
};

type OrderTypesState = {
  products: CategoryState;
  services: CategoryState;
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  products: 'For anything you stock or sell — including raw material, fabrics and finished goods.',
  services: 'For service-based work like alteration, cleaning, repair or installation.',
};

const DEFAULT_STATE: OrderTypesState = {
  products: { enabled: true, types: [] },
  services: { enabled: true, types: [] },
};

const OrderTypes = () => {
  const navigate = useNavigate();
  const addCustomModalRef = useRef<ModalMethods>(null);

  const [state, setState] = useState<OrderTypesState>(DEFAULT_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [addCustomCategory, setAddCustomCategory] = useState<'products' | 'services'>('products');
  const [addCustomName, setAddCustomName] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);

  const fetchOrderTypes = useCallback(async () => {
    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!boutique_id) {
      toasts('error', 'Boutique ID not found', 'boutique-id-error');
      setIsLoading(false);

      return;
    }

    try {
      const response = await api.getRequest(`api/boutiques/${boutique_id}/order-types`);

      if (response.status && response.data) {
        const data = response.data;
        setState({
          products: {
            enabled: data.products?.enabled ?? true,
            types: data.products?.types ?? [],
          },
          services: {
            enabled: data.services?.enabled ?? true,
            types: data.services?.types ?? [],
          },
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'order-types-fetch-error');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrderTypes();
  }, [fetchOrderTypes]);

  const handleCategoryToggle = (category: 'products' | 'services') => {
    setState((prev) => ({
      ...prev,
      [category]: { ...prev[category], enabled: !prev[category].enabled },
    }));
  };

  const handleTypeToggle = (category: 'products' | 'services', typeId: number) => {
    setState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        types: prev[category].types.map((t) => (t.id === typeId ? { ...t, active: !t.active } : t)),
      },
    }));
  };

  const handleSave = async () => {
    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!boutique_id) {
      toasts('error', 'Boutique ID not found', 'boutique-id-error');

      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        products: {
          enabled: state.products.enabled,
          activeTypeIds: state.products.types.filter((t) => t.active).map((t) => t.id),
        },
        services: {
          enabled: state.services.enabled,
          activeTypeIds: state.services.types.filter((t) => t.active).map((t) => t.id),
        },
      };

      const response = await api.putRequest(`api/boutiques/${boutique_id}/order-types`, payload);

      if (response.status) {
        toasts('success', 'Order types saved successfully', 'order-types-saved');
        navigate('/settings');
      } else {
        toasts('error', 'Failed to save order types', 'order-types-error');
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'order-types-error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const openAddCustom = (category: 'products' | 'services') => {
    setAddCustomCategory(category);
    setAddCustomName('');
    addCustomModalRef.current?.show();
  };

  const handleAddCustomClose = () => {
    addCustomModalRef.current?.hide();
    setAddCustomName('');
  };

  const handleAddCustomSubmit = async () => {
    const name = addCustomName?.trim();

    if (!name) {
      toasts('error', 'Please enter a name for the order type', 'add-custom-name-error');

      return;
    }

    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!boutique_id) {
      toasts('error', 'Boutique ID not found', 'boutique-id-error');

      return;
    }

    setIsAddingCustom(true);

    try {
      const categoryPayload = addCustomCategory === 'products' ? 'PRODUCTS' : 'SERVICES';
      const response = await api.postRequest(`api/boutiques/${boutique_id}/order-types`, {
        category: categoryPayload,
        name,
      });

      if (response.status) {
        addCustomModalRef.current?.hide();
        setAddCustomName('');
        await fetchOrderTypes();
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, 'add-custom-error');
      }
    } finally {
      setIsAddingCustom(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-white p-6 flex items-center justify-center">
        <Loader showLoader={true} />
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white px-6 py-2">
      <div className="flex items-center justify-between mb-4 border-1 border-[#32323226] py-1">
        <p className="text-gray-700 font-inter font-semibold leading-none tracking-normal text-[1rem]">
          Please select your default order Types
        </p>
        <Button
          label="Save"
          leadingIcon={<FaSave size={14} />}
          onClick={handleSave}
          bgColor="primary"
          size="medium"
          disabled={isSaving}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {(['products', 'services'] as const).map((categoryKey) => {
          const category = state[categoryKey];
          const title = categoryKey === 'products' ? 'Products' : 'Services';

          return (
            <div key={categoryKey} className="bg-gray-100 rounded-lg p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-800 font-inter font-semibold text-sm leading-none tracking-normal text-[1.1rem] ">
                  {title}
                </p>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={category.enabled}
                    onChange={() => handleCategoryToggle(categoryKey)}
                    className="sr-only peer"
                  />
                  <div className="w-[37px] h-[17px] bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer relative peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:transition-all after:w-[13px] after:h-[13px] peer-checked:after:translate-x-[20px]"></div>
                </label>
              </div>

              <p className="text-[#000000B2] mb-4 font-inter font-medium text-sm leading-5 tracking-normal">
                {CATEGORY_DESCRIPTIONS[categoryKey]}
              </p>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {category.types.map((typeItem) => (
                  <button
                    key={typeItem.id}
                    type="button"
                    onClick={() => handleTypeToggle(categoryKey, typeItem.id)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg bg-white border border-gray-200 text-left font-inter text-sm font-medium text-gray-900 hover:border-gray-300 transition-colors"
                  >
                    <span
                      className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded border ${
                        typeItem.active
                          ? 'bg-[#4D7AFF] border-blue-600 text-white'
                          : 'bg-white border-gray-300'
                      }`}
                    >
                      {typeItem.active ? (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </span>
                    <span className="truncate">{typeItem.name}</span>
                  </button>
                ))}
              </div>

              <Button
                label="Add Custom"
                leadingIcon={<FaPlus size={12} className="text-gray-700" />}
                onClick={() => openAddCustom(categoryKey)}
                appearance="outlined"
                size="small"
                className="justify-center bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200 font-semibold"
              />
            </div>
          );
        })}
      </div>

      <Modal
        ref={addCustomModalRef}
        size="small"
        title="Add Custom Order Type"
        showFooter={true}
        closeButtonText="Cancel"
        saveButtonText="Add"
        isSaveEnabled={!isAddingCustom && !!addCustomName?.trim()}
        onModalClose={handleAddCustomClose}
        onModalSuccess={handleAddCustomSubmit}
      >
        <div className="space-y-4">
          <p className="text-gray-600 font-inter text-sm">
            Add a new order type for {addCustomCategory === 'products' ? 'Products' : 'Services'}.
          </p>
          <Input
            type="text"
            label="Name"
            value={addCustomName}
            onChange={(e) => setAddCustomName(e.target.value)}
            placeholder="e.g. Custom Buttons"
            disabled={isAddingCustom}
          />
        </div>
      </Modal>

      <Loader showLoader={isSaving} />
    </div>
  );
};

export default OrderTypes;
